import saveAs from "file-saver";
import Papa from "papaparse";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import {
  FileValidationParams,
  validateAndReadFile,
} from "../utils/validateFileHeaders";

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export interface IExport {
  id: string;
  usedBy: string;
  vendorConnectionProfileId: string;
  createdBy: number;
  createdDate: Date;
  status: string;
  attachmentId?: number;
  fileName?: string;
}
export type CostPlanCostComparison = "GT" | "LT" | "EQ";

export interface CostPlanType {
  id: string;
  cost: number;
  effectiveDate: Date;
  comment: string;
  isDeleted?: boolean;
  newCost: number;
  updatedBy?: string;
  operatorId: number;
  vendorConnectionProfileId: string;
  /** GET /routing-costs list filter */
  costValue?: number;
  costComparison?: CostPlanCostComparison;
}

export interface CostPlanHistory {
  id: string;
  snapshot: CostPlanType & { timestamp: string };
  historyTimestamp: string;
  mainId: number;
  status: string;
  createdBy: string;
}

export interface CostPlanHistoryDiff {
  historyTimestamp: string;
  mainId: number;
  createdBy: string;
  snapshot: CostPlanType & { timestamp: string };
  diff?: Record<string, string>;
}

export const exportCostPlan = async (
  params: Params<CostPlanType> = {}
): Promise<void> => {
  try {
    const response = await axiosInstance.get("/routing-costs/csv", {
      params,
      responseType: "blob",
    });

    // Extract filename from Content-Disposition header
    const disposition = response.headers["content-disposition"];
    let filename = "CostPlan.csv";
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "text/csv" });
    saveAs(blob, filename);
  } catch (error: any) {
    console.error(
      "Error fetching CostPlan :",
      error.response?.data?.message || error.message || error
    );
  }
};

export async function fetchCostPlan(
  params: Params<CostPlanType>
): Promise<IPageable<CostPlanType>> {
  try {
    const response = await axiosInstance.get("/routing-costs", { params });
    const data = response.data;
    return data;
  } catch (error: any) {
    console.error("Error fetching routing costs:", error);
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching routing costs."
    );
  }
}

export const addCostPlanApi = async (data: Partial<CostPlanType>) => {
  try {
    const response = await axiosInstance.post("/routing-costs/upsert", data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the routing cost."
    );
  }
};

export const editCostPlanApi = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/routing-costs/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while updating the routing cost."
    );
  }
};

export const getCostPlanById = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/routing-costs/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching the routing cost."
    );
  }
};

export const deleteCostPlanApi = async (ids: string[]): Promise<void> => {
  try {
    await axiosInstance.delete(`/routing-costs`, { data: ids });
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while deleting the routing cost."
    );
  }
};

export interface CostPlanUploadParams {
  mccmnc: string;
  mcc: string;
  mnc: string;
  // rateCount: string;
  rate: string;
  effectiveDate: string;
  operatorName: string;
  countryName: string;
  destinationHeader: string;
}

export const uploadCostPlansExcel = async (
  vendorConnectionProfileId: string,
  file: File,
  override: boolean,
  uploadParams: CostPlanUploadParams
): Promise<void> => {
  const extension = file.name.split(".").pop()?.toLowerCase() || "xlsx";

  const validationParams: FileValidationParams = {
    alwaysRequired: [uploadParams.rate],
    optionalFields: [
      uploadParams.mccmnc,
      uploadParams.mcc,
      uploadParams.mnc,
      uploadParams.effectiveDate,
      uploadParams.operatorName,
      uploadParams.countryName,
      uploadParams.destinationHeader,
    ],
  };

  // Read the whole file, validate headers, and get all rows
  const rows = await validateAndReadFile(file, extension, validationParams);

  // Re-encode as a frontend-generated CSV for a clean, safe payload
  const csvString = Papa.unparse(rows);
  const csvBlob = new Blob([csvString], { type: "text/csv" });
  const csvFileName = file.name.replace(/\.[^.]+$/, ".csv");
  const csvFile = new File([csvBlob], csvFileName, { type: "text/csv" });

  const formData = new FormData();
  formData.append("file", csvFile);

  const params = {
    vendorConnectionProfileId,
    override,
    ...(uploadParams.mcc ? { mccHeader: uploadParams.mcc } : {}),
    ...(uploadParams.mnc ? { mncHeader: uploadParams.mnc } : {}),
    ...(uploadParams.mccmnc ? { mccmncHeader: uploadParams.mccmnc } : {}),
    ...(uploadParams.effectiveDate
      ? { effectiveDateHeader: uploadParams.effectiveDate }
      : {}),
    ...(uploadParams.operatorName
      ? { operatorHeader: uploadParams.operatorName }
      : {}),
    ...(uploadParams.countryName
      ? { countryHeader: uploadParams.countryName }
      : {}),
    ...(uploadParams.destinationHeader
      ? { destinationHeader: uploadParams.destinationHeader }
      : {}),
    rateHeader: uploadParams.rate,
    effectiveDateHeader: uploadParams.effectiveDate,
  };

  try {
    const response = await axiosInstance.post(
      `/routing-costs/csv`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        params,
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while uploading the routing cost file."
    );
  }
};

export const useCostPlanQuery = (
  params: Params<CostPlanType>
): UseQueryResult<IPageable<CostPlanType>, Error> => {
    const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
  };
  const queryKey = ["costPlan", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchCostPlan(effectiveParams),
  });
};

export const CostPlanBulkApi = async (data: Partial<CostPlanType>[]) => {
  try {
    const Data = data.map(({ id, ...rest }) => rest);
    const response = await axiosInstance.put("/routing-costs", Data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the Cost Plan."
    );
  }
};

export async function fetchImportedFiles(
  params: Params<IExport>
): Promise<IPageable<IExport>> {
  try {
    const finalParams = {
      ...params,
      sort: params.sort || "createdDate,desc",
    };
    console.log("Fetching imported files with params:", finalParams);
    const response = await axiosInstance.get("/imported-files", {
      params: finalParams,
    });
    const data = response.data;
    return data;
  } catch (error: any) {
    console.error("Error fetching routing costs:", error);
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching routing costs."
    );
  }
}

export const useImportedFilesQuery = (
  params: Params<IExport>
): UseQueryResult<IPageable<IExport>, Error> => {
  const queryKey = ["importedFiles", JSON.stringify(params)];
  return useQuery({
    queryKey,
    queryFn: () => fetchImportedFiles(params),
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 1000,
  });
};

export async function fetchCostPlanHistory(
  id: CostPlanType["id"]
): Promise<IPageable<CostPlanType & CostPlanHistory>> {
  try {
    const response = await axiosInstance.get(`/routing-costs/history`, {
      params: { mainId: id, sort: "historyTimestamp,desc" },
    });
    const data = response.data;
    return {
      ...data,
      content: data.content.map((item: any) => ({
        ...item.snapshot,
        historyTimestamp: item.historyTimestamp,
      })),
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting the routing-Cost history for ID ${id}.`
    );
  }
}

export const useNoDataQuery = (
  params: Params<CostPlanType> = {}
): UseQueryResult<IPageable<CostPlanType>, Error> => {
  const { comment = "random text to fetch no result", ...rest } = params;
  const fullParams = { comment, ...rest };
  const queryKey = ["routingPlanDetails", JSON.stringify(fullParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchCostPlan(fullParams),
  });
};
